<script setup lang="ts">
import { computed, ref } from 'vue'
import { projects } from '../../data/projects'
import { useWorkView } from '../../composables/useWorkView'
import { revealDirective } from '../../composables/useReveal'
import type { Project } from '../../types/projects'

defineOptions({ directives: { reveal: revealDirective } })

const { open } = useWorkView()

/*
  Six projects don't need faceted filtering — three dropdowns for six items is
  more chrome than content. A single category row does the job.
*/
const categories = computed(() => [
  'All',
  ...Array.from(new Set(projects.map((p) => p.category))),
])
const activeCategory = ref('All')

const visible = computed(() =>
  activeCategory.value === 'All'
    ? projects
    : projects.filter((p) => p.category === activeCategory.value)
)

/* ---- hover preview: the payoff for keeping the list flat ---- */
const peek = ref<{ src: string; x: number; y: number } | null>(null)
const supportsPeek =
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches

function onEnter(project: Project, e: PointerEvent) {
  const src = project.images?.[0] ?? project.image
  if (!supportsPeek || !src) return
  peek.value = { src, x: e.clientX, y: e.clientY }
}

function onMove(e: PointerEvent) {
  if (peek.value) {
    peek.value = { ...peek.value, x: e.clientX, y: e.clientY }
  }
}

function onLeave() {
  peek.value = null
}

const peekStyle = computed(() => {
  if (!peek.value) return {}
  const width = 304
  return {
    left: `${Math.min(peek.value.x + 200, window.innerWidth - width / 2 - 24)}px`,
    top: `${peek.value.y}px`,
  }
})

function displayNumber(project: Project) {
  return String(projects.indexOf(project) + 1).padStart(2, '0')
}
</script>

<template>
  <section id="work" class="section cv-auto">
    <div class="wrap">
      <div class="section-head" v-reveal>
        <h2 class="display display-3">Work, in order of consequence</h2>
        <p class="eyebrow">
          {{ visible.length }} of {{ projects.length }}
        </p>
      </div>

      <div class="filters" role="tablist" aria-label="Filter work by category">
        <button
          v-for="category in categories"
          :key="category"
          class="filter meta"
          role="tab"
          :aria-selected="activeCategory === category"
          @click="activeCategory = category"
        >
          {{ category }}
        </button>
      </div>

      <ul class="index" @pointerleave="onLeave">
        <li v-for="project in visible" :key="project.id">
          <button
            class="row"
            @click="open(project.id)"
            @pointerenter="onEnter(project, $event)"
            @pointermove="onMove"
          >
            <span class="num meta">{{ displayNumber(project) }}</span>

            <span class="body">
              <span class="display display-3 title">{{ project.title }}</span>
              <span class="blurb">{{ project.problem ?? project.description }}</span>
            </span>

            <span class="stack meta">
              {{ project.technologies.slice(0, 2).join(' · ') }}
            </span>

            <span class="cat meta">{{ project.year ?? project.category }}</span>

            <span class="arrow meta" aria-hidden="true">→</span>
          </button>
        </li>
      </ul>

      <p v-if="!visible.length" class="empty prose-body">
        Nothing in that category yet.
      </p>
    </div>

    <!-- cursor-following preview -->
    <Transition name="peek">
      <div v-if="peek" class="peek" :style="peekStyle" aria-hidden="true">
        <img :src="peek.src" alt="" />
      </div>
    </Transition>
  </section>
</template>

<style scoped>
.section-head {
  margin-bottom: 1.2rem;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 1.3rem;
  padding-block: 0.9rem 1.6rem;
}
.filter {
  background: none;
  border: 0;
  padding: 0.15rem 0;
  cursor: pointer;
  color: var(--c-ink-3);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  font-size: 0.68rem;
  transition: color 0.2s ease;
  position: relative;
}
.filter:hover {
  color: var(--c-ink);
}
.filter[aria-selected='true'] {
  color: var(--c-ink);
}
.filter[aria-selected='true']::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -3px;
  height: 1px;
  background: var(--c-accent);
}

.index {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--c-rule-strong);
}

.row {
  display: grid;
  grid-template-columns: 2.6rem 1fr;
  gap: 0 1.2rem;
  align-items: baseline;
  width: 100%;
  text-align: left;
  padding: 1.4rem 0;
  background: none;
  border: 0;
  border-bottom: 1px solid var(--c-rule);
  cursor: pointer;
  transition: background-color 0.25s ease;
}
@media (min-width: 900px) {
  .row {
    grid-template-columns: 2.6rem minmax(0, 1fr) 11rem 6rem 1.6rem;
  }
}
.row:hover {
  background: color-mix(in srgb, var(--c-surface-sunken) 45%, transparent);
}

.num {
  color: var(--c-ink-3);
  padding-top: 0.45rem;
}
.body {
  display: block;
}
.title {
  display: block;
  margin-bottom: 0.4rem;
  transition: color 0.25s ease;
}
.blurb {
  display: block;
  color: var(--c-ink-2);
  font-size: 0.92rem;
  max-width: 54ch;
}
.stack,
.cat {
  padding-top: 0.5rem;
  color: var(--c-ink-2);
  line-height: 1.7;
}
.cat {
  color: var(--c-ink-3);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.66rem;
}
.arrow {
  padding-top: 0.45rem;
  color: var(--c-ink-3);
  transition: transform 0.3s var(--ease-out-quint), color 0.3s ease;
}
@media (max-width: 899px) {
  .stack,
  .cat {
    grid-column: 2;
  }
  .arrow {
    display: none;
  }
}

.row:hover .title {
  color: var(--c-accent);
}
.row:hover .arrow {
  transform: translateX(0.3rem);
  color: var(--c-accent);
}

.empty {
  padding-block: 2.5rem;
}

/* ---- preview ---- */
.peek {
  position: fixed;
  z-index: 40;
  width: 19rem;
  aspect-ratio: 16 / 10;
  translate: -50% -50%;
  overflow: hidden;
  pointer-events: none;
  background: var(--c-surface-sunken);
  border: 1px solid var(--c-rule);
  border-radius: 3px;
  box-shadow: var(--shadow-pop);
}
.peek img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: left top;
}
/* light UI screenshots glare on the dark surface — same dim as the scene */
[data-theme='dark'] .peek img {
  filter: brightness(0.75) contrast(1.04);
}
.peek-enter-active,
.peek-leave-active {
  transition: opacity 0.2s ease, scale 0.28s var(--ease-out-quint);
}
.peek-enter-from,
.peek-leave-to {
  opacity: 0;
  scale: 0.96;
}
</style>
